import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCurrentUser,
  getReferenceData,
  updateCurrentUser,
} from "../api/budgetPortal";
import { useAuth } from "../state/AuthContext";
import { APP_NAME } from "../constants/app";
import { ButtonLoader } from "../components/Loaders";
import Logo from "../assets/image/logo.svg";

const defaultForm = {
  organization_name: "",
  fy_start: "",
  fy_end: "",
  physical_area: "",
  physical_locality: "",
  physical_state: "",
  physical_country: "",
  physical_zip: "0000",
  postal_area: "",
  postal_locality: "",
  postal_state: "",
  postal_country: "",
  postal_zip: "0000",
  currency: "",
  logo: "",
};

const getFinancialYearEnd = (startDate) => {
  if (!startDate) {
    return "";
  }

  const date = new Date(startDate);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  date.setDate(date.getDate() - 1);
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().split("T")[0];
};

const SetupPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { token, logout, updateUser } = useAuth();
  const [form, setForm] = useState(defaultForm);
  const [sameAddress, setSameAddress] = useState(true);
  const [countries, setCountries] = useState([]);
  const [selectedImage, setSelectedImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const countryOptions = useMemo(() => countries ?? [], [countries]);

  useEffect(() => {
    const loadSetupData = async () => {
      setLoading(true);
      setError("");

      try {
        const [profileResponse, referenceResponse] = await Promise.all([
          getCurrentUser(token),
          getReferenceData(token),
        ]);
        const profile = profileResponse?.data ?? {};
        const organization = profile.organization ?? {};
        const nextCountries = referenceResponse?.data?.countries ?? [];
        const predictedCountry =
          referenceResponse?.data?.predicted_data?.country ??
          nextCountries[0]?.name ??
          "";
        const predictedCurrency =
          referenceResponse?.data?.predicted_data?.currency ??
          nextCountries[0]?.currency ??
          "";
        const physicalArea = organization.physical_area ?? "";
        const physicalLocality = organization.physical_locality ?? "";
        const physicalState = organization.physical_state ?? "";
        const physicalCountry = organization.physical_country || predictedCountry;
        const physicalZip = organization.physical_zip || "0000";

        updateUser(profile);
        setCountries(nextCountries);
        setForm({
          ...defaultForm,
          organization_name: organization.name ?? "",
          fy_start: organization.fy_start ?? "",
          fy_end: organization.fy_end ?? "",
          physical_area: physicalArea,
          physical_locality: physicalLocality,
          physical_state: physicalState,
          physical_country: physicalCountry,
          physical_zip: physicalZip,
          postal_area: organization.postal_area || physicalArea,
          postal_locality: organization.postal_locality || physicalLocality,
          postal_state: organization.postal_state || physicalState,
          postal_country: organization.postal_country || physicalCountry,
          postal_zip: organization.postal_zip || physicalZip,
          currency: organization.currency || predictedCurrency,
          logo: "",
        });
      } catch (setupError) {
        setError(
          setupError?.response?.data?.message ||
          setupError.message ||
          "Unable to load setup information.",
        );
      }

      setLoading(false);
    };

    loadSetupData();
  }, [token, updateUser]);

  const updateField = (field, value) => {
    setForm((currentValue) => {
      const nextValue = {
        ...currentValue,
        [field]: value,
      };

      if (field === "fy_start") {
        nextValue.fy_end = getFinancialYearEnd(value);
      }

      if (sameAddress && field.startsWith("physical_")) {
        const postalField = field.replace("physical_", "postal_");
        nextValue[postalField] = value;
      }

      return nextValue;
    });
  };

  const handleCountryChange = (countryName) => {
    const country = countryOptions.find((item) => item.name === countryName);
    setForm((currentValue) => ({
      ...currentValue,
      physical_country: countryName,
      postal_country: countryName,
      currency: country?.currency ?? currentValue.currency,
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = `${reader.result}`;
      setSelectedImage(result);
      setForm((currentValue) => ({
        ...currentValue,
        logo: result.split(",")[1] ?? "",
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      await updateCurrentUser(token, form);
      const profileResponse = await getCurrentUser(token);
      updateUser(profileResponse?.data ?? null);
      navigate("/restore-backup", {
        replace: true,
        state: { fromSetup: true },
      });
    } catch (submitError) {
      setError(
        submitError?.response?.data?.message ||
        submitError.message ||
        "Unable to save setup.",
      );
    }

    setLoading(false);
  };

  return (
    <section className="setup-layout">
      <nav className="setup-nav">
        <div className="setup-nav-logoContainer">
          <img src={Logo} alt={APP_NAME} className="setup-nav-logo" />
          <p className="setup-nav-title">{APP_NAME}</p>
        </div>

        <button
          type="button"
          className="setup-nav-logoutContainer"
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
        >
          <span className="setup-nav-logout">Logout</span>
        </button>
      </nav>

      <div className="setup-container">
        <div className="setup-titleLogoContainer">
          <div>
            <p className="setup-title">Setup</p>
            <p className="setup-secondTitle">
              Fill in the informations of your organisation/entity
            </p>
          </div>

          <button
            type="button"
            className="setup-logoSelector"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleLogoUpload}
            />
            {selectedImage ? (
              <img src={selectedImage} alt="Selected Logo" className="setup-logoImage" />
            ) : (
              <span className="setup-logoSelector-title">Upload Logo</span>
            )}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="setup-form-container">
            <div className="setup-form-nameDateContainer">
              <div>
                <label className="setup-input-text" htmlFor="organization_name">
                  Name of organisation/entity *
                </label>
                <input
                  id="organization_name"
                  className="setup-textinput-left"
                  required
                  minLength={3}
                  maxLength={64}
                  value={form.organization_name}
                  onChange={(event) =>
                    updateField("organization_name", event.target.value)
                  }
                />

                <div className="setup-textinput-countryContainer">
                  <div>
                    <label className="setup-input-text" htmlFor="physical_country">
                      Country *
                    </label>
                    <select
                      id="physical_country"
                      className="setup-textinput-leftSmall"
                      required
                      value={form.physical_country}
                      onChange={(event) => handleCountryChange(event.target.value)}
                    >
                      <option value="">Select country</option>
                      {countryOptions.map((country) => (
                        <option key={country.name} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="setup-input-text" htmlFor="currency">
                      Currency *
                    </label>
                    <select
                      id="currency"
                      className="setup-textinput-leftSmall"
                      required
                      value={form.currency}
                      onChange={(event) => updateField("currency", event.target.value)}
                    >
                      <option value="">Select currency</option>
                      {countryOptions.map((country) => (
                        <option key={`${country.name}-${country.currency}`} value={country.currency}>
                          {country.currency}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="setup-form-rightarea-dateContainer">
                <div>
                  <label className="setup-input-text" htmlFor="fy_start">
                    Financial Year Start *
                  </label>
                  <input
                    id="fy_start"
                    className="setup-textinput-calendar"
                    type="date"
                    required
                    value={form.fy_start}
                    onChange={(event) => updateField("fy_start", event.target.value)}
                  />
                </div>

                <div>
                  <label className="setup-input-text" htmlFor="fy_end">
                    Financial Year End *
                  </label>
                  <input
                    id="fy_end"
                    className="setup-textinput-calendar"
                    type="date"
                    required
                    readOnly
                    value={form.fy_end}
                  />
                </div>
              </div>
            </div>

            <div className="setup-form-addressContainer">
              <div className="setup-form-address">
                <p className="setup-input-text">
                  Physical address of organisation/entity *
                </p>
                <input
                  className="setup-textinput-left"
                  placeholder="Building"
                  required
                  value={form.physical_area}
                  onChange={(event) => updateField("physical_area", event.target.value)}
                />
                <input
                  className="setup-textinput-left"
                  placeholder="Locality"
                  required
                  value={form.physical_locality}
                  onChange={(event) =>
                    updateField("physical_locality", event.target.value)
                  }
                />
                <input
                  className="setup-textinput-leftSmall"
                  placeholder="State / Province"
                  required
                  value={form.physical_state}
                  onChange={(event) => updateField("physical_state", event.target.value)}
                />
                <input
                  className="setup-textinput-leftSmall"
                  placeholder="Zip / Postal Code"
                  required
                  minLength={4}
                  value={form.physical_zip}
                  onChange={(event) => updateField("physical_zip", event.target.value)}
                />
                <label className="setup-checkboxContainer">
                  <input
                    className="setup-checkbox"
                    type="checkbox"
                    checked={sameAddress}
                    onChange={(event) => {
                      setSameAddress(event.target.checked);
                      if (event.target.checked) {
                        setForm((currentValue) => ({
                          ...currentValue,
                          postal_area: currentValue.physical_area,
                          postal_locality: currentValue.physical_locality,
                          postal_state: currentValue.physical_state,
                          postal_country: currentValue.physical_country,
                          postal_zip: currentValue.physical_zip,
                        }));
                      }
                    }}
                  />
                  <span>Use same address for Postal Adress as well.</span>
                </label>
              </div>

              <div className="setup-form-address">
                <p className="setup-input-text">
                  Postal address of organisation/entity *
                </p>
                <input
                  className="setup-textinput-left"
                  placeholder="Building"
                  required
                  readOnly={sameAddress}
                  value={form.postal_area}
                  onChange={(event) => updateField("postal_area", event.target.value)}
                />
                <input
                  className="setup-textinput-left"
                  placeholder="Locality"
                  required
                  readOnly={sameAddress}
                  value={form.postal_locality}
                  onChange={(event) =>
                    updateField("postal_locality", event.target.value)
                  }
                />
                <input
                  className="setup-textinput-leftSmall"
                  placeholder="State / Province"
                  required
                  readOnly={sameAddress}
                  value={form.postal_state}
                  onChange={(event) => updateField("postal_state", event.target.value)}
                />
                <input
                  className="setup-textinput-leftSmall"
                  placeholder="Zip / Postal Code"
                  required
                  minLength={4}
                  readOnly={sameAddress}
                  value={form.postal_zip}
                  onChange={(event) => updateField("postal_zip", event.target.value)}
                />
              </div>
            </div>
          </div>

          {error ? <div className="web-inlineError">{error}</div> : null}

          <button type="submit" className="submit-button-setup" disabled={loading}>
            <span className="submit-button-text-setup">
              {loading ? <ButtonLoader label="Saving..." /> : "Continue"}
            </span>
          </button>
        </form>
      </div>
    </section>
  );
};

export default SetupPage;
